import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i0 from '@angular/core';
import { Injectable, InjectionToken, EventEmitter, Component, ViewEncapsulation, ChangeDetectionStrategy, SkipSelf, Inject, Input, Output, HostBinding, NgModule } from '@angular/core';
import { randomId } from '@yukawa/chain-base-angular-client';
import { yukawaAnimations } from '@yukawa/chain-base-angular-comp/animations';
import { ReplaySubject, Subject, filter, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { uniqueId } from 'lodash-es';
import * as i1 from '@ngneat/transloco';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i4 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i5 from '@yukawa/chain-base-angular-comp/shared';
import { SharedModule } from '@yukawa/chain-base-angular-comp/shared';

class YukawaAlertService {
    /**
     * Constructor
     */
    constructor(_translocoService) {
        this._translocoService = _translocoService;
        this._onDismiss = new ReplaySubject(1);
        this._onShow = new ReplaySubject(1);
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------
    /**
     * Getter for onDismiss
     */
    get onDismiss() {
        return this._onDismiss.asObservable();
    }
    /**
     * Getter for onShow
     */
    get onShow() {
        return this._onShow.asObservable();
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    /**
     * Generates an alert configuration from the provided error object.
     * Uses translation keys `ERROR.NO_INTERNET_CONNECTION` and `ERROR.UNKNOWN`.
     *
     * @param error
     * @param  environment
     */
    generateErrorAlert(error, environment) {
        let message = this._translocoService.translate(!window.navigator.onLine
            ? 'ERROR.NO_INTERNET_CONNECTION'
            : 'ERROR.UNKNOWN');
        if (!environment.production) {
            message += '\n\n';
            if (typeof error === 'string') {
                message += error;
            }
            else {
                message += `${error.message}`;
                if (error instanceof HttpErrorResponse && error.error) {
                    message += `\n\n${error.error.text}`;
                }
            }
        }
        return {
            name: uniqueId('app_alert_'),
            type: 'error',
            message,
        };
    }
    /**
     * Dismiss the alert
     *
     * @param name
     */
    dismiss(name) {
        // Return if the name is not provided
        if (!name) {
            return;
        }
        // Execute the observable
        this._onDismiss.next(name);
    }
    /**
     * Show the dismissed alert
     *
     * @param name
     * @param config
     */
    show(name, config) {
        // Return if the name is not provided
        if (!name) {
            return;
        }
        // Execute the observable
        this._onShow.next(!config ? name : Object.assign(Object.assign({}, config), { name }));
    }
}
/** @nocollapse */ YukawaAlertService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: YukawaAlertService, deps: [{ token: i1.TranslocoService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ YukawaAlertService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: YukawaAlertService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: YukawaAlertService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.TranslocoService }]; } });

const YUKAWA_ALERT_ICON_CONFIG = new InjectionToken('YUKAWA_ALERT_ICON_CONFIG');
class YukawaAlertComponent {
    /**
     * Constructor
     */
    constructor(_changeDetectorRef, _parentChangeDetectorRef, _yukawaAlertService, iconConfig) {
        this._changeDetectorRef = _changeDetectorRef;
        this._parentChangeDetectorRef = _parentChangeDetectorRef;
        this._yukawaAlertService = _yukawaAlertService;
        this.iconConfig = iconConfig;
        /* eslint-enable @typescript-eslint/naming-convention */
        this.appearance = 'soft';
        this.dismissed = false;
        this.dismissible = false;
        this.showIcon = true;
        this.type = 'primary';
        this.dismissedChanged = new EventEmitter();
        this._unsubscribeAll = new Subject();
        this.name = randomId();
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------
    /**
     * Host binding for component classes
     */
    get classList() {
        return {
            /* eslint-disable @typescript-eslint/naming-convention */
            'yukawa-alert-appearance-border': this.appearance === 'border',
            'yukawa-alert-appearance-fill': this.appearance === 'fill',
            'yukawa-alert-appearance-outline': this.appearance === 'outline',
            'yukawa-alert-appearance-soft': this.appearance === 'soft',
            'yukawa-alert-dismissed': this.dismissed,
            'yukawa-alert-dismissible': this.dismissible,
            'yukawa-alert-show-icon': this.showIcon,
            'yukawa-alert-type-primary': this.type === 'primary',
            'yukawa-alert-type-accent': this.type === 'accent',
            'yukawa-alert-type-warn': this.type === 'warn',
            'yukawa-alert-type-basic': this.type === 'basic',
            'yukawa-alert-type-info': this.type === 'info',
            'yukawa-alert-type-success': this.type === 'success',
            'yukawa-alert-type-warning': this.type === 'warning',
            'yukawa-alert-type-error': this.type === 'error',
            /* eslint-enable @typescript-eslint/naming-convention */
        };
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------
    /**
     * On changes
     *
     * @param changes
     */
    ngOnChanges(changes) {
        // Dismissed
        if ('dismissed' in changes) {
            // Coerce the value to a boolean
            this.dismissed = coerceBooleanProperty(changes.dismissed.currentValue);
            // Dismiss/show the alert
            this._toggleDismiss(this.dismissed);
        }
        // Dismissible
        if ('dismissible' in changes) {
            // Coerce the value to a boolean
            this.dismissible = coerceBooleanProperty(changes.dismissible.currentValue);
        }
        // Show icon
        if ('showIcon' in changes) {
            // Coerce the value to a boolean
            this.showIcon = coerceBooleanProperty(changes.showIcon.currentValue);
        }
    }
    /**
     * On init
     */
    ngOnInit() {
        // Subscribe to the dismiss calls
        this._yukawaAlertService.onDismiss
            .pipe(filter(name => this.name === name), takeUntil(this._unsubscribeAll))
            .subscribe(() => {
            // Dismiss the alert
            this.dismiss();
        });
        // Subscribe to the show calls
        this._yukawaAlertService.onShow
            .pipe(filter(name => typeof name === 'object' ? this.name === name.name : this.name === name), takeUntil(this._unsubscribeAll))
            .subscribe((name) => {
            if (typeof name === 'object') {
                Object.assign(this, name);
            }
            // Show the alert
            this.show();
        });
    }
    /**
     * On destroy
     */
    ngOnDestroy() {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    /**
     * Dismiss the alert
     */
    dismiss(self) {
        if (self) {
            this._yukawaAlertService.dismiss(this.name);
            return;
        }
        // Return if the alert is already dismissed
        if (this.dismissed) {
            return;
        }
        // Dismiss the alert
        this._toggleDismiss(true);
    }
    /**
     * Show the dismissed alert
     */
    show() {
        // Return if the alert is already showing
        if (!this.dismissed) {
            return;
        }
        // Show the alert
        this._toggleDismiss(false);
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------
    /**
     * Dismiss/show the alert
     *
     * @param dismissed
     * @private
     */
    _toggleDismiss(dismissed) {
        // Return if the alert is not dismissible
        if (!this.dismissible) {
            return;
        }
        // Set the dismissed
        this.dismissed = dismissed;
        // Execute the observable
        this.dismissedChanged.next(this.dismissed);
        // Notify the change detector
        this._changeDetectorRef.detectChanges();
        this._parentChangeDetectorRef.detectChanges();
    }
}
/** @nocollapse */ YukawaAlertComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: YukawaAlertComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ChangeDetectorRef, skipSelf: true }, { token: YukawaAlertService }, { token: YUKAWA_ALERT_ICON_CONFIG }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ YukawaAlertComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: YukawaAlertComponent, selector: "yukawa-alert", inputs: { appearance: "appearance", dismissed: "dismissed", dismissible: "dismissible", name: "name", showIcon: "showIcon", type: "type", message: "message" }, outputs: { dismissedChanged: "dismissedChanged" }, host: { properties: { "class": "this.classList" } }, exportAs: ["yukawaAlert"], usesOnChanges: true, ngImport: i0, template: "<div\n    class=\"yukawa-alert-container overflow-hidden\"\n    *ngIf=\"!dismissible || dismissible && !dismissed\"\n    [@fadeIn]=\"!dismissed\"\n    [@fadeOut]=\"!dismissed\">\n\n    <!-- Border -->\n    <div\n        class=\"yukawa-alert-border\"\n        *ngIf=\"appearance === 'border'\"></div>\n\n    <!-- Icon -->\n    <div\n        class=\"yukawa-alert-icon\"\n        *ngIf=\"showIcon\">\n\n        <!-- Custom icon -->\n        <div class=\"yukawa-alert-custom-icon\">\n            <ng-content select=\"[yukawaAlertIcon]\"></ng-content>\n        </div>\n\n        <!-- Default icons -->\n        <div class=\"yukawa-alert-default-icon\">\n\n            <mat-icon\n                *ngIf=\"type === 'primary'\"\n                [svgIcon]=\"iconConfig.primary\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'accent'\"\n                [svgIcon]=\"iconConfig.accent\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'warn'\"\n                [svgIcon]=\"iconConfig.warn\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'basic'\"\n                [svgIcon]=\"iconConfig.basic\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'info'\"\n                [svgIcon]=\"iconConfig.info\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'success'\"\n                [svgIcon]=\"iconConfig.success\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'warning'\"\n                [svgIcon]=\"iconConfig.warning\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'error'\"\n                [svgIcon]=\"iconConfig.error\"></mat-icon>\n\n        </div>\n\n    </div>\n\n    <!-- Content -->\n    <div class=\"yukawa-alert-content overflow-auto\">\n\n        <div class=\"yukawa-alert-title\">\n            <ng-content select=\"[yukawaAlertTitle]\"></ng-content>\n        </div>\n\n        <div class=\"yukawa-alert-message\">\n            <ng-container *ngIf=\"message\">\n                <div [innerHTML]=\"message | marked\"></div>\n            </ng-container>\n            <ng-content></ng-content>\n        </div>\n\n    </div>\n\n    <!-- Dismiss button -->\n    <button\n        class=\"yukawa-alert-dismiss-button\"\n        mat-icon-button\n        (click)=\"dismiss(true)\">\n        <mat-icon [svgIcon]=\"iconConfig.dismiss\"></mat-icon>\n    </button>\n\n</div>\n", dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i3.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }, { kind: "component", type: i4.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "pipe", type: i5.MarkedPipe, name: "marked" }], animations: yukawaAnimations, changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: YukawaAlertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yukawa-alert', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, animations: yukawaAnimations, exportAs: 'yukawaAlert', template: "<div\n    class=\"yukawa-alert-container overflow-hidden\"\n    *ngIf=\"!dismissible || dismissible && !dismissed\"\n    [@fadeIn]=\"!dismissed\"\n    [@fadeOut]=\"!dismissed\">\n\n    <!-- Border -->\n    <div\n        class=\"yukawa-alert-border\"\n        *ngIf=\"appearance === 'border'\"></div>\n\n    <!-- Icon -->\n    <div\n        class=\"yukawa-alert-icon\"\n        *ngIf=\"showIcon\">\n\n        <!-- Custom icon -->\n        <div class=\"yukawa-alert-custom-icon\">\n            <ng-content select=\"[yukawaAlertIcon]\"></ng-content>\n        </div>\n\n        <!-- Default icons -->\n        <div class=\"yukawa-alert-default-icon\">\n\n            <mat-icon\n                *ngIf=\"type === 'primary'\"\n                [svgIcon]=\"iconConfig.primary\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'accent'\"\n                [svgIcon]=\"iconConfig.accent\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'warn'\"\n                [svgIcon]=\"iconConfig.warn\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'basic'\"\n                [svgIcon]=\"iconConfig.basic\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'info'\"\n                [svgIcon]=\"iconConfig.info\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'success'\"\n                [svgIcon]=\"iconConfig.success\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'warning'\"\n                [svgIcon]=\"iconConfig.warning\"></mat-icon>\n\n            <mat-icon\n                *ngIf=\"type === 'error'\"\n                [svgIcon]=\"iconConfig.error\"></mat-icon>\n\n        </div>\n\n    </div>\n\n    <!-- Content -->\n    <div class=\"yukawa-alert-content overflow-auto\">\n\n        <div class=\"yukawa-alert-title\">\n            <ng-content select=\"[yukawaAlertTitle]\"></ng-content>\n        </div>\n\n        <div class=\"yukawa-alert-message\">\n            <ng-container *ngIf=\"message\">\n                <div [innerHTML]=\"message | marked\"></div>\n            </ng-container>\n            <ng-content></ng-content>\n        </div>\n\n    </div>\n\n    <!-- Dismiss button -->\n    <button\n        class=\"yukawa-alert-dismiss-button\"\n        mat-icon-button\n        (click)=\"dismiss(true)\">\n        <mat-icon [svgIcon]=\"iconConfig.dismiss\"></mat-icon>\n    </button>\n\n</div>\n" }]
        }], ctorParameters: function () {
        return [{ type: i0.ChangeDetectorRef }, { type: i0.ChangeDetectorRef, decorators: [{
                        type: SkipSelf
                    }] }, { type: YukawaAlertService }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [YUKAWA_ALERT_ICON_CONFIG]
                    }] }];
    }, propDecorators: { appearance: [{
                type: Input
            }], dismissed: [{
                type: Input
            }], dismissible: [{
                type: Input
            }], name: [{
                type: Input
            }], showIcon: [{
                type: Input
            }], type: [{
                type: Input
            }], message: [{
                type: Input
            }], dismissedChanged: [{
                type: Output
            }], classList: [{
                type: HostBinding,
                args: ['class']
            }] } });

class YukawaAlertModule {
    static forRoot(iconConfig = {
        primary: 'check_circle',
        accent: 'check_circle',
        warn: 'warning',
        basic: 'check_circle',
        info: 'info',
        success: 'check_circle',
        warning: 'warning',
        error: 'error',
        dismiss: 'close',
    }) {
        return {
            ngModule: YukawaAlertModule,
            providers: [
                {
                    provide: YUKAWA_ALERT_ICON_CONFIG,
                    useValue: iconConfig,
                },
            ],
        };
    }
}
/** @nocollapse */ YukawaAlertModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: YukawaAlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ YukawaAlertModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: YukawaAlertModule, declarations: [YukawaAlertComponent], imports: [CommonModule,
        MatButtonModule,
        MatIconModule,
        SharedModule], exports: [YukawaAlertComponent] });
/** @nocollapse */ YukawaAlertModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: YukawaAlertModule, imports: [CommonModule,
        MatButtonModule,
        MatIconModule,
        SharedModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: YukawaAlertModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        YukawaAlertComponent,
                    ],
                    imports: [
                        CommonModule,
                        MatButtonModule,
                        MatIconModule,
                        SharedModule,
                    ],
                    exports: [
                        YukawaAlertComponent,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { YUKAWA_ALERT_ICON_CONFIG, YukawaAlertComponent, YukawaAlertModule, YukawaAlertService };
